import { useState, useEffect, useCallback } from "react";


import useWindowSize from "../hooks/useWindowSize";
import apiService from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import AssignCaregiverModal from "../components/AssignCaregiverModal";
import ConfirmationModal from "../components/ConfirmationModal";
import NotificationModal from "../components/NotificationModal";
import { invalidateDashboardCache } from "./HomePage"; // Import cache invalidation function
import profileIcon from "../assets/seniors.svg";
import trashIcon from "../assets/Trash.svg";
import "../styles/pages/SeniorsPage.css";

const W = "#ffffff";

/* ═══════════════════════ FILTER BUTTON COMPONENT (EXPORTED) ═══════════════════════ */
export function FilterButton({ label, value, options, onChange, isMobile }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="filter-button-container">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`filter-button ${value !== "All" ? "active" : ""} ${isMobile ? "mobile" : ""}`}
      >
        <span className="filter-button__label">{label}:</span>
        <span>{value}</span>
        <svg 
          className={`filter-button__arrow ${isOpen ? "open" : ""}`}
          width="12" 
          height="12" 
          viewBox="0 0 24 24" 
          fill="none"
        >
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            onClick={() => setIsOpen(false)}
            className="filter-dropdown-overlay"
          />
          <div className="filter-dropdown">
            {options.map((option) => (
              <button
                key={option}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className={`filter-dropdown__option ${value === option ? "active" : ""} ${isMobile ? "mobile" : ""}`}
              >
                {option}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════ PAGE ═══════════════════════ */
export default function SeniorsPage({ onFiltersChange, onResidentClick, onAddSenior, onEditResident, refreshTrigger }) {
  const [residents, setResidents] = useState([]);
  const [filteredResidents, setFilteredResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("All");
  const [selectedSleepQuality, setSelectedSleepQuality] = useState("All");
    const [selectedRiskLevel, setSelectedRiskLevel] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

    // Assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedResidentForAssignment, setSelectedResidentForAssignment] = useState(null);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [residentToDelete, setResidentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Notification modal state
  const [notification, setNotification] = useState({ show: false, type: 'info', message: '' });

  // Pass filter state to parent (Dashboard) so Sidebar can display them
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange({
        selectedAgeGroup,
        setSelectedAgeGroup,
        selectedSleepQuality,
        setSelectedSleepQuality,
        selectedRiskLevel,
        setSelectedRiskLevel,
      });
    }
  }, [selectedAgeGroup, selectedSleepQuality, selectedRiskLevel, onFiltersChange]);
  
  const { isMobile } = useWindowSize();

    useEffect(() => {
    loadResidents();
  }, [refreshTrigger]);

  const loadResidents = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAllResidents();
      
      
      
      // Check if we have any residents
      if (!data || !data.residents || data.residents.length === 0) {
        console.warn('No residents data received from API');
        setResidents([]);
        return;
      }
      
      // Transform the data to extract needed fields from database
      const transformedResidents = data.residents.map((r, index) => {
        
        
        // Extract name from status_name_sort field: "ACTIVE--LastName--FirstName--RES--..."
        let displayName = 'Unknown Resident';
        if (r.status_name_sort) {
          const parts = r.status_name_sort.split('--');
          if (parts.length >= 3) {
            const lastName = parts[1] || '';
            const firstName = parts[2] || '';
            displayName = `${firstName} ${lastName}`.trim();
          }
        }
        
        // Clean room_id - remove ROOM- prefix
        const roomNumber = (r.room_id || '')
          .replace(/^ROOM-/, '') || 'N/A';
        
        return {
          resident_id: r.resident_id || `RES-unknown-${index}`,
          name: displayName, // Extract from database field
          room_id: roomNumber,
          age: parseInt(r.age) || 0,
          fall_risk_level: r.fall_risk_level || 'MODERATE',
          latest_sleep_quality: r.latest_sleep_quality || 'AVERAGE',
          age_group: r.age_group || 'N/A',
          status: r.status || 'ACTIVE',
          mrn: r.mrn || 'N/A'
        };
      });
      
      
      setResidents(transformedResidents);
    } catch (err) {
      console.error('Failed to load residents:', err.message);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const filterResidents = useCallback(() => {
    let filtered = [...residents];

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        (r.name || "").toLowerCase().includes(search) ||
        (r.resident_id || "").toLowerCase().includes(search) ||
        (r.room_id || "").toString().toLowerCase().includes(search)
      );
    }

    // Age group filter
    if (selectedAgeGroup !== "All") {
      filtered = filtered.filter(r => {
        const age = r.age || 0;
        switch(selectedAgeGroup) {
          case "60-70": return age >= 60 && age <= 70;
          case "71-80": return age >= 71 && age <= 80;
          case "81+": return age >= 81;
          default: return true;
        }
      });
    }

    // Sleep quality filter
    if (selectedSleepQuality !== "All") {
      filtered = filtered.filter(r => r.latest_sleep_quality === selectedSleepQuality);
    }

    // Risk level filter
    if (selectedRiskLevel !== "All") {
      filtered = filtered.filter(r => r.fall_risk_level === selectedRiskLevel);
    }

    setFilteredResidents(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [residents, searchTerm, selectedAgeGroup, selectedSleepQuality, selectedRiskLevel]);

  // Filter residents when filters or search term changes
  useEffect(() => {
    filterResidents();
  }, [filterResidents]);

    const handleDeleteResident = (resident) => {
      setResidentToDelete(resident);
      setShowDeleteModal(true);
    };

    const confirmDeleteResident = async () => {
      if (!residentToDelete) return;

      try {
        setDeleteLoading(true);
        await apiService.deleteResident(residentToDelete.resident_id);
      
        // Invalidate dashboard cache so it refreshes on next visit
        invalidateDashboardCache();

        // Remove from local state so the list updates instantly
        setResidents(residents.filter(r => r.resident_id !== residentToDelete.resident_id));
      
        setShowDeleteModal(false);
        setResidentToDelete(null);
      
        setNotification({
          show: true,
          type: 'success',
          message: 'Resident deleted successfully!'
        });
      } catch (err) {
        console.error('Failed to delete resident:', err.message);
      
        setNotification({
          show: true,
          type: 'error',
          message: 'Failed to delete resident. Please try again.'
        });
      } finally {
        setDeleteLoading(false);
      }
    };

  const handleAssignCaregiver = (resident) => {
    setSelectedResidentForAssignment(resident);
    setShowAssignModal(true);
  };

    const handleAssignmentComplete = async () => {
      console.log('Assignment completed successfully - reloading residents...');
    
      // Invalidate HomePage cache so it refreshes on next visit
      invalidateDashboardCache();
    
      // Reload residents to get updated data
      await loadResidents();
    
      // Show success notification
      setNotification({
        show: true,
        type: 'success',
        message: 'Caregiver assigned successfully!'
      });
    };

    if (loading) {
    return (
      <main className="seniors-loading-container">
        <LoadingSpinner message="Loading residents..." />
      </main>
    );
  }

  if (error) {
    return (
      <main className="seniors-loading-container">
        <ErrorMessage error={error} onRetry={loadResidents} />
      </main>
    );
  }

    // Responsive layout values
  const gap = isMobile ? 8 : 10;

  // Pagination calculations
  const totalPages = Math.ceil(filteredResidents.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredResidents.slice(indexOfFirstRecord, indexOfLastRecord);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
        <main style={{
      flex: 1,
      padding: isMobile ? "8px" : "12px 60px",
      overflowY: "hidden",
      display: "flex",
      flexDirection: "column",
      gap,
      minWidth: 0,
      fontFamily: "'Segoe UI', sans-serif",
      color: W,
      position: "relative",
      zIndex: 1,
      paddingTop: isMobile ? 75 : 82,
      paddingBottom: isMobile ? 20 : 6,
      marginTop: 0,
      height: "100vh",
      boxSizing: "border-box",
    }}>
      
            {/* ═══ SEARCH BAR & ADD SENIOR BUTTON ═══ */}
      <div className={`seniors-search-bar ${isMobile ? "mobile" : ""}`}>
        {/* "Seniors" Label Button */}
        <button className="seniors-label-btn">
          Seniors
        </button>

        {/* Search Bar */}
        <div className={`seniors-search-input-wrapper ${isMobile ? "mobile" : ""}`}>
          {/* Profile Icon Circle */}
          <div className="seniors-search-icon">
            <svg width={24} height={24} viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="8" r="4"/>
              <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/>
            </svg>
          </div>

          {/* Input Field */}
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="seniors-search-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="seniors-search-clear"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>

        {/* Add Senior Label (text only) */}
        <span 
          onClick={() => onAddSenior && onAddSenior()}
          className="seniors-add-label"
        >
          Add Senior
        </span>

        {/* Add Senior Button */}
        <div className="seniors-add-button-wrapper">
          <div className="seniors-add-button-outer" />
          <button 
            onClick={() => onAddSenior && onAddSenior()}
            className="seniors-add-button-inner"
            title="Add Senior"
          >
            <img 
              src={profileIcon} 
              alt="Add Senior"
            />
          </button>
        </div>
      </div>

                  {/* ═══ RESIDENTS GRID ═══ */}
      <div className={`seniors-grid-container ${isMobile ? "mobile" : ""}`}>
        {/* Residents Grid */}
        <div className={`seniors-grid ${isMobile ? "mobile" : ""}`}>
          {currentRecords.length === 0 ? (
            <div className="seniors-empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 8v4M12 16h.01"/>
              </svg>
              <p>
                {searchTerm ? "No residents found matching your search" : "No residents available"}
              </p>
            </div>
          ) : (
                        currentRecords.map((resident) => (
              <ResidentCard
                key={resident.resident_id}
                resident={resident}
                onDelete={handleDeleteResident}
                onEdit={onEditResident}
                onAssign={handleAssignCaregiver}
                onClick={() => onResidentClick && onResidentClick(resident.resident_id)}
                isMobile={isMobile}
              />
            ))
          )}
        </div>

                        {/* Pagination Controls - Always Visible */}
        {filteredResidents.length > 0 && (
          <div className={`seniors-pagination ${isMobile ? "mobile" : ""}`}>
            {/* Info Text */}
            <span className={`seniors-pagination__info ${isMobile ? "mobile" : ""}`}>
              Showing {indexOfFirstRecord + 1} - {Math.min(indexOfLastRecord, filteredResidents.length)} of {filteredResidents.length} residents
            </span>

            {/* Page Controls - Always show even if only 1 page */}
            <div className="seniors-pagination__controls">
                            {/* Previous Button with Arrow */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="seniors-pagination__nav-btn seniors-pagination__nav-btn--prev"
              >
                {/* Left Arrow Icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
                {!isMobile && <span>Prev</span>}
              </button>

                            {/* Page Numbers */}
              {totalPages > 1 && Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => {
                // Show first page, last page, current page, and pages around current
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`seniors-pagination__page-btn ${currentPage === pageNumber ? "seniors-pagination__page-btn--active" : ""}`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return (
                    <span key={pageNumber} className="seniors-pagination__ellipsis">
                      •••
                    </span>
                  );
                }
                return null;
              })}

                            {/* Next Button with Arrow */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="seniors-pagination__nav-btn seniors-pagination__nav-btn--next"
              >
                {!isMobile && <span>Next</span>}
                {/* Right Arrow Icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

            {/* Assignment Modal */}
      {showAssignModal && selectedResidentForAssignment && (
        <AssignCaregiverModal
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedResidentForAssignment(null);
          }}
          residentId={selectedResidentForAssignment.resident_id}
          residentName={selectedResidentForAssignment.name}
          onAssignmentComplete={handleAssignmentComplete}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={confirmDeleteResident}
        onCancel={() => {
          setShowDeleteModal(false);
          setResidentToDelete(null);
        }}
        title="Delete Resident"
        message={`Are you sure you want to remove ${residentToDelete?.name || 'this resident'}? This will also delete their photo and emergency contacts.`}
        confirmText="Delete Resident"
        cancelText="Cancel"
        variant="danger"
        loading={deleteLoading}
        isMobile={isMobile}
        details={
          residentToDelete && (
            <>
              <div><strong>Resident ID:</strong> {residentToDelete.resident_id}</div>
              <div><strong>Room:</strong> {residentToDelete.room_id}</div>
              <div><strong>MRN:</strong> {residentToDelete.mrn}</div>
            </>
          )
        }
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        type={notification.type}
        message={notification.message}
        autoDismiss={3}
        isMobile={isMobile}
      />
    </main>
  );
}

/* ═══════════════════════ RESIDENT CARD COMPONENT ═══════════════════════ */
function ResidentCard({ resident, onDelete, onEdit, onAssign, onClick, isMobile }) {
  const [editLoading, setEditLoading] = useState(false);

  const handleEditClick = async (e) => {
    e.stopPropagation();
    try {
      setEditLoading(true);
      const fullResidentData = await apiService.getResidentDetails(resident.resident_id);
      onEdit && onEdit(fullResidentData);
    } catch (err) {
      console.error('Failed to fetch resident details for edit:', err.message);
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div 
      onClick={onClick}
      className="resident-card"
    >
      {/* Profile Picture */}
      <div className="resident-card__avatar">
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="8" r="4"/>
          <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/>
        </svg>
      </div>

      {/* Resident Name - Centered */}
      <div className="resident-card__name-wrapper">
        <p className="resident-card__name">
          {resident.name || "Unknown Resident"}
        </p>
      </div>

      {/* Edit Button */}
      <button
        onClick={handleEditClick}
        className="resident-card__edit-btn"
        title="Edit resident"
        disabled={editLoading}
      >
        {editLoading ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            style={{ animation: "spin 1s linear infinite" }}>
            <path d="M21 12a9 9 0 11-6.219-8.56"/>
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        )}
      </button>

      {/* Assign Caregiver Button (+) */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onAssign && onAssign(resident);
        }}
        className="resident-card__assign-btn"
        title="Assign caregiver"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14M5 12h14"/>
        </svg>
      </button>

            {/* Delete Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(resident);
        }}
        className="resident-card__delete-btn"
        title="Remove resident"
      >
        <img src={trashIcon} alt="Delete" />
      </button>
    </div>
  );
}
